import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Activity,
  Pencil,
  AlertTriangle,
  Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { THERAPY_OPTIONS, getTherapyConfig } from "../lib/therapyTypes";
import { updateProfile } from "../lib/api";

function FieldChips({ items }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((label, i) => (
        <li
          key={`${label}-${i}`}
          className="font-figtree text-[12px] px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#0F172A] border border-slate-200"
        >
          {i + 1}. {label}
        </li>
      ))}
    </ul>
  );
}

export default function AccountPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const currentType = user?.therapy_type || "physiotherapy";
  const cfg = getTherapyConfig(currentType);

  const [name, setName] = useState(user?.name || "");
  const [clinic, setClinic] = useState(user?.clinic_name || "");
  const [busy, setBusy] = useState(false);

  // Therapy-type change dialog state
  const [changeOpen, setChangeOpen] = useState(false);
  const [pendingType, setPendingType] = useState(currentType);
  const [confirming, setConfirming] = useState(false);

  if (!user) return null;

  const pendingCfg = getTherapyConfig(pendingType);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const updated = await updateProfile({ name, clinic_name: clinic });
      setUser(updated);
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Couldn't save", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const openChange = () => {
    setPendingType(currentType);
    setChangeOpen(true);
  };

  const confirmChange = async () => {
    if (pendingType === currentType) {
      setChangeOpen(false);
      return;
    }
    setConfirming(true);
    try {
      const updated = await updateProfile({ therapy_type: pendingType });
      setUser(updated);
      toast.success("Therapy type updated", {
        description: `New plans will use ${updated.therapy_type
          .replace(/^./, (c) => c.toUpperCase())} field labels.`,
      });
      setChangeOpen(false);
    } catch (e) {
      toast.error("Couldn't update", { description: e.message });
    } finally {
      setConfirming(false);
    }
  };

  const trialBadge = () => {
    if (user.tier === "solo") {
      if (user.solo_days_left != null && user.solo_days_left <= 5)
        return {
          text: `Renews in ${user.solo_days_left}d`,
          tone: "bg-amber-100 text-amber-700",
        };
      return { text: "Solo plan", tone: "bg-[#ECFDF5] text-[#065F46]" };
    }
    if (user.trial_expired)
      return { text: "Trial expired", tone: "bg-red-100 text-red-700" };
    return {
      text: `${user.trial_days_left ?? 0} days left in trial`,
      tone: "bg-amber-100 text-amber-700",
    };
  };
  const badge = trialBadge();

  const fmtFullDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="account-page">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/app")}
            data-testid="account-back-btn"
            className="inline-flex items-center gap-2 font-figtree text-sm text-[#475569] hover:text-[#0F172A]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </button>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#10B981] text-white">
              <Activity className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="font-outfit text-base font-bold text-[#0F172A]">
              TherapyNote
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 space-y-8">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight text-[#0F172A]">
            Account
          </h1>
          <p className="font-figtree text-sm text-[#64748B] mt-1">
            Manage your profile, clinic, subscription, and dictation field setup.
          </p>
        </div>

        {/* Subscription card */}
        <section
          data-testid="account-subscription-card"
          className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-figtree text-xs uppercase tracking-[0.18em] text-[#94A3B8]">
                Subscription
              </div>
              <div className="font-outfit text-lg font-semibold text-[#0F172A] mt-1">
                {user.tier === "solo"
                  ? "Solo · $149 / month"
                  : "15-day Free Trial"}
              </div>
            </div>
            <span
              data-testid="account-subscription-badge"
              className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.tone}`}
            >
              {badge.text}
            </span>
          </div>

          {/* Renewal / expiry callout */}
          {user.tier === "solo" && user.solo_until && (
            <div
              data-testid="account-renewal-callout"
              className={`mt-4 rounded-2xl border p-4 flex items-start gap-3 ${
                user.solo_days_left != null && user.solo_days_left <= 5
                  ? "border-amber-200 bg-amber-50"
                  : "border-[#A7F3D0] bg-[#ECFDF5]"
              }`}
            >
              <Calendar
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  user.solo_days_left != null && user.solo_days_left <= 5
                    ? "text-amber-700"
                    : "text-[#059669]"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div
                  data-testid="account-renewal-label"
                  className="font-outfit text-sm font-semibold text-[#0F172A]"
                >
                  Solo access through {fmtFullDate(user.solo_until)}
                </div>
                <p className="font-figtree text-xs text-[#475569] mt-0.5">
                  {user.solo_days_left} day{user.solo_days_left === 1 ? "" : "s"} remaining.
                  Pay again to extend by another 30 days — your time stacks
                  from your current end date.
                </p>
              </div>
            </div>
          )}
          {user.tier === "trial" && user.trial_expires_at && !user.trial_expired && (
            <div
              data-testid="account-trial-callout"
              className="mt-4 rounded-2xl border border-slate-200 bg-[#FAFAFA] p-4 flex items-start gap-3"
            >
              <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#64748B]" />
              <div className="flex-1 min-w-0">
                <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                  Trial ends {fmtFullDate(user.trial_expires_at)}
                </div>
                <p className="font-figtree text-xs text-[#475569] mt-0.5">
                  {user.trial_days_left ?? 0} day{user.trial_days_left === 1 ? "" : "s"} remaining.
                  Upgrade anytime — there's no break in access.
                </p>
              </div>
            </div>
          )}
          {user.tier === "trial" && user.trial_expired && (
            <div
              data-testid="account-expired-callout"
              className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3"
            >
              <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-700" />
              <div className="flex-1 min-w-0">
                <div className="font-outfit text-sm font-semibold text-[#7F1D1D]">
                  Your trial ended on {fmtFullDate(user.trial_expires_at)}
                </div>
                <p className="font-figtree text-xs text-[#991B1B] mt-0.5">
                  Existing plans stay viewable. Pay $149 to unlock a fresh
                  month of Solo and resume saving new plans.
                </p>
              </div>
            </div>
          )}

          <p className="font-figtree text-xs text-[#64748B] mt-3">
            {user.tier === "solo"
              ? "Renewals are manual right now — we'll send you a reminder a few days before your access ends."
              : user.tier === "trial" && !user.trial_expired
              ? "Solo and Clinic subscriptions are launching shortly. You'll see them here once they're live."
              : "Your trial has ended. Subscriptions are launching shortly — we'll let you know as soon as Solo is live."}
          </p>

          <div
            data-testid="account-coming-soon-cta"
            className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-slate-100 text-slate-500 font-figtree text-xs font-medium cursor-not-allowed"
          >
            Subscriptions coming soon
          </div>
        </section>

        {/* Profile */}
        <section className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6 space-y-4">
          <div>
            <div className="font-outfit text-base font-semibold text-[#0F172A]">
              Profile
            </div>
            <div className="font-figtree text-xs text-[#64748B] mt-0.5">
              Signed in as {user.email}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-figtree text-xs text-[#475569]">
                Your name
              </Label>
              <Input
                data-testid="account-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
              />
            </div>
            <div>
              <Label className="font-figtree text-xs text-[#475569]">
                Clinic
              </Label>
              <Input
                data-testid="account-clinic"
                value={clinic}
                onChange={(e) => setClinic(e.target.value)}
                className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              data-testid="account-save-btn"
              onClick={saveProfile}
              disabled={busy}
              className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save profile"
              )}
            </Button>
          </div>
        </section>

        {/* Field setup */}
        <section
          data-testid="account-field-setup"
          className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-figtree text-xs uppercase tracking-[0.18em] text-[#94A3B8]">
                Your dictation fields
              </div>
              <div className="font-outfit text-lg font-semibold text-[#0F172A] mt-1">
                {cfg.label}
              </div>
              <p className="font-figtree text-xs text-[#64748B] mt-1 max-w-md">
                These are the field labels TherapyNote routes your voice into on
                the Evaluation and Daily Notes tabs.
              </p>
            </div>
            <Button
              data-testid="account-change-therapy-btn"
              onClick={openChange}
              variant="outline"
              className="rounded-full border-slate-200 hover:bg-slate-50 font-figtree text-sm"
            >
              <Pencil className="w-3.5 h-3.5 mr-2" />
              Change
            </Button>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div
              data-testid="field-setup-evaluation"
              className="rounded-2xl border border-slate-200 bg-[#FAFAFA] p-4"
            >
              <div className="font-figtree text-[11px] uppercase tracking-[0.16em] text-[#065F46] font-semibold">
                Evaluation tab
              </div>
              <div className="mt-2.5">
                <FieldChips items={cfg.evaluation} />
              </div>
            </div>
            <div
              data-testid="field-setup-daily"
              className="rounded-2xl border border-slate-200 bg-[#FAFAFA] p-4"
            >
              <div className="font-figtree text-[11px] uppercase tracking-[0.16em] text-[#065F46] font-semibold">
                Daily Notes tab
              </div>
              <div className="mt-2.5">
                <FieldChips items={cfg.daily} />
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Change therapy type dialog */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent
          data-testid="change-therapy-dialog"
          className="max-w-2xl rounded-3xl p-0 overflow-hidden"
        >
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-outfit text-xl tracking-tight text-[#0F172A]">
              Change therapy type
            </DialogTitle>
            <DialogDescription className="font-figtree text-sm text-[#64748B]">
              Pick the discipline you actually practise. Field labels and AI
              extraction will update for new plans.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 max-h-[45vh] overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-2.5">
              {THERAPY_OPTIONS.map((opt) => {
                const active = pendingType === opt.value;
                return (
                  <button
                    key={opt.value}
                    data-testid={`change-therapy-${opt.value}`}
                    aria-pressed={active}
                    onClick={() => setPendingType(opt.value)}
                    className={`relative text-left rounded-2xl border p-4 transition-all ${
                      active
                        ? "border-[#10B981] bg-[#ECFDF5]"
                        : "border-slate-200 hover:border-[#A7F3D0]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                        {opt.label}
                      </div>
                      {active && (
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                    <p className="font-figtree text-[11px] text-[#94A3B8] mt-2">
                      Fields: {opt.evaluation.join(" · ")}
                    </p>
                  </button>
                );
              })}
            </div>

            {pendingType !== currentType && (
              <div
                data-testid="change-therapy-warning"
                className="mt-5 rounded-2xl border border-[#FCD34D] bg-[#FFFBEB] p-4 flex items-start gap-3"
              >
                <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#FDE68A] text-[#92400E]">
                  <AlertTriangle className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="font-outfit text-sm font-semibold text-[#7C2D12]">
                    Heads up — this only changes new plans
                  </div>
                  <p className="font-figtree text-xs text-[#7C2D12] leading-relaxed">
                    From now on, every plan you create will use the{" "}
                    <strong>{pendingCfg.label}</strong> field labels and AI
                    extraction. Existing saved plans will keep the labels they
                    were originally created with — nothing in your patient
                    history is rewritten.
                  </p>
                  <p className="font-figtree text-[11px] text-[#92400E] mt-1">
                    New labels — Evaluation: {pendingCfg.evaluation.join(", ")}
                    . Daily Notes: {pendingCfg.daily.join(", ")}.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 bg-[#FAFAFA] flex items-center justify-between flex-wrap gap-3">
            <p className="font-figtree text-xs text-[#94A3B8]">
              Currently: <strong>{cfg.label}</strong>
            </p>
            <div className="flex gap-2">
              <Button
                data-testid="change-therapy-cancel-btn"
                variant="outline"
                onClick={() => setChangeOpen(false)}
                className="rounded-full border-slate-200 hover:bg-slate-50 font-figtree text-sm"
              >
                Cancel
              </Button>
              <Button
                data-testid="change-therapy-confirm-btn"
                onClick={confirmChange}
                disabled={confirming || pendingType === currentType}
                className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6 disabled:opacity-50"
              >
                {confirming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating…
                  </>
                ) : (
                  "Confirm change"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
